import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { APIHOST } from '../../../common';

interface OrderRoomItem {
  id?: number | string;
  documentId?: string;
  type?: string | null;
  conference_hotel_room?: {
    name?: string | null;
  } | null;
  order?: {
    orderStatus?: string | null;
    conference_hotel?: {
      hotel?: {
        name?: string | null;
      } | null;
    } | null;
  } | null;
}

interface StrapiListResponse<T> {
  data: T[];
  meta?: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface HotelSummary {
  hotelName: string;
  roomTypeCounts: Record<string, number>;
  total: number;
}

export default function HotelRoomReport() {
  const [orderRooms, setOrderRooms] = useState<OrderRoomItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderRooms = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const allRows: OrderRoomItem[] = [];
        let page = 1;
        let pageCount = 1;

        while (page <= pageCount) {
          const response = await fetch(
            `${APIHOST}/api/order-rooms?filters[order][orderStatus][$eq]=received&populate[conference_hotel_room]=true&populate[order][populate][conference_hotel][populate][hotel]=true&pagination[page]=${page}&pagination[pageSize]=200`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error('Failed to fetch order room report data');
          }

          const payload: StrapiListResponse<OrderRoomItem> = await response.json();
          const rows = Array.isArray(payload?.data) ? payload.data : [];

          allRows.push(...rows);

          pageCount = payload?.meta?.pagination?.pageCount || 1;
          page += 1;
        }

        // Keep an explicit local filter as a guard in case backend query behavior changes.
        setOrderRooms(allRows.filter((row) => row?.order?.orderStatus === 'received'));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderRooms();
  }, []);

  const hotelSummaries = useMemo<HotelSummary[]>(() => {
    const grouped: Record<string, HotelSummary> = {};

    for (const row of orderRooms) {
      const hotelName = row?.order?.conference_hotel?.hotel?.name || 'Unknown Hotel';
      const roomType = row?.conference_hotel_room?.name || 'Unknown';

      if (!grouped[hotelName]) {
        grouped[hotelName] = {
          hotelName,
          roomTypeCounts: {},
          total: 0,
        };
      }

      grouped[hotelName].roomTypeCounts[roomType] = (grouped[hotelName].roomTypeCounts[roomType] || 0) + 1;
      grouped[hotelName].total += 1;
    }

    return Object.values(grouped).sort((a, b) => a.hotelName.localeCompare(b.hotelName));
  }, [orderRooms]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (hotelSummaries.length === 0) {
    return <Alert severity="info">No received room bookings found.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5">Hotel Room Booking Report</Typography>

      {hotelSummaries.map((summary) => {
        const roomTypes = Object.entries(summary.roomTypeCounts).sort((a, b) => a[0].localeCompare(b[0]));

        return (
          <Paper key={summary.hotelName} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {summary.hotelName}
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Room Type</TableCell>
                  <TableCell align="right">Bookings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roomTypes.map(([roomType, count]) => (
                  <TableRow key={`${summary.hotelName}-${roomType}`}>
                    <TableCell>{roomType}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {summary.total}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        );
      })}
    </Box>
  );
}
